import Button from "../../../../../shared/components/Button";
import InfoTooltip from "../../../../../shared/components/InfoTooltip";
import Spinner from "../../../../../shared/components/Spinner";
import { siteTexts } from "../../../../../shared/i18n/siteTexts";
import type {
  OrganizerRankingPointsTable,
  OrganizerRankingSettings,
} from "../../../../auth/shared/types/auth";
import ProfileAccordionSection from "../ProfileAccordionSection";
import UserProfileSectionHeader from "../UserProfileSectionHeader";

type OrganizerRankingMode = "tnp_standard" | "custom";

type OrganizerRankingFieldGroup = {
  modality: keyof OrganizerRankingPointsTable;
  icon: string;
  fields: Array<{
    key: string;
    labelKey: string;
  }>;
};

type ProfileOrganizerRankingTabProps = {
  open: boolean;
  onToggle: () => void;
  fields: OrganizerRankingFieldGroup[];
  settings: OrganizerRankingSettings | null;
  draft: OrganizerRankingPointsTable | null;
  mode: OrganizerRankingMode;
  modeDescriptions: Record<OrganizerRankingMode, string>;
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;
  onModeChange: (mode: OrganizerRankingMode) => void;
  onDuplicateOfficial: () => void;
  onUpdatePoint: (
    modality: keyof OrganizerRankingPointsTable,
    fieldKey: string,
    value: string,
  ) => void;
  onRestoreDefault: () => void;
  onSave: () => void;
};

export default function ProfileOrganizerRankingTab({
  open,
  onToggle,
  fields,
  settings,
  draft,
  mode,
  modeDescriptions,
  loading,
  saving,
  error,
  success,
  onModeChange,
  onDuplicateOfficial,
  onUpdatePoint,
  onRestoreDefault,
  onSave,
}: ProfileOrganizerRankingTabProps) {
  return (
    <ProfileAccordionSection
      id="profile-organizer-ranking"
      icon="leaderboard"
      title={siteTexts.profile.user.organizerRanking.title}
      subtitle={siteTexts.profile.user.organizerRanking.description}
      open={open}
      onToggle={onToggle}
    >
      <section className="user-profile-organizer-ranking">
        <UserProfileSectionHeader
          icon="leaderboard"
          title={
            <span className="info-label">
              {siteTexts.profile.user.organizerRanking.title}
              <InfoTooltip content={siteTexts.profile.user.organizerRanking.info} />
            </span>
          }
          description={siteTexts.profile.user.organizerRanking.description}
        />

        <fieldset className="user-profile-organizer-ranking-modes">
          <legend>{siteTexts.profile.user.organizerRanking.scoringMode}</legend>
          {(["tnp_standard", "custom"] as const).map((nextMode) => {
            const isSelected = mode === nextMode;
            const isDisabled = saving || loading;
            return (
              <label
                key={nextMode}
                className={`user-profile-ranking-mode-card${
                  isSelected ? " is-selected" : ""
                }${isDisabled ? " is-disabled" : ""}`}
              >
                <input
                  type="radio"
                  name="organizer-ranking-mode"
                  checked={isSelected}
                  onChange={() => onModeChange(nextMode)}
                  disabled={isDisabled}
                />
                <span className="user-profile-ranking-mode-copy">
                  <strong>
                    {siteTexts.profile.user.organizerRanking.modes[nextMode]}
                  </strong>
                  <small>{modeDescriptions[nextMode]}</small>
                </span>
              </label>
            );
          })}
        </fieldset>

        {loading && (
          <Spinner label={siteTexts.profile.user.organizerRanking.loading} />
        )}

        <div className="user-profile-organizer-ranking-alert">
          <span className="material-symbols-outlined" aria-hidden="true">
            info
          </span>
          <div>
            <strong>Informações</strong>
            <p>
              Esta tabela afeta apenas o ranking dos eventos organizados por
              você e não modifica o Ranking Oficial do TNP.
            </p>
          </div>
        </div>

        {mode === "custom" && (
          <div className="user-profile-organizer-ranking-toolbar">
            <Button
              type="button"
              variant="pill"
              onClick={onDuplicateOfficial}
              disabled={saving || loading || !settings?.officialPoints}
            >
              {siteTexts.profile.user.organizerRanking.duplicateOfficial}
            </Button>
          </div>
        )}

        {draft && (
          <div className="user-profile-organizer-ranking-grid">
            {fields.map((group) => (
              <section
                className="user-profile-organizer-ranking-group"
                key={group.modality}
              >
                <div className="user-profile-organizer-ranking-group-title">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    {group.icon}
                  </span>
                  <h4>
                    {
                      siteTexts.profile.user.organizerRanking.modalities[
                        group.modality
                      ]
                    }
                  </h4>
                </div>
                <div>
                  {group.fields.map((field) => {
                    const value =
                      draft[group.modality][
                        field.key as keyof (typeof draft)[typeof group.modality]
                      ];
                    const isReadOnly = mode === "tnp_standard";
                    return (
                      <label
                        className="field"
                        key={`${group.modality}-${field.key}`}
                      >
                        <span>
                          {
                            siteTexts.profile.user.organizerRanking.fields[
                              field.labelKey as keyof typeof siteTexts.profile.user.organizerRanking.fields
                            ]
                          }
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={String(value ?? "")}
                          readOnly={isReadOnly}
                          disabled={isReadOnly || saving || loading}
                          onChange={(event) =>
                            onUpdatePoint(
                              group.modality,
                              field.key,
                              event.target.value,
                            )
                          }
                        />
                      </label>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        {error && <p className="status-message">{error}</p>}
        {success && <p className="status-message">{success}</p>}

        {mode === "custom" && (
          <div className="user-profile-actions">
            <Button
              type="button"
              variant="pill"
              onClick={onRestoreDefault}
              disabled={saving || loading}
            >
              {siteTexts.profile.user.organizerRanking.restoreDefault}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={onSave}
              disabled={saving || loading || !draft}
            >
              {saving
                ? siteTexts.profile.user.organizerRanking.saving
                : siteTexts.profile.user.organizerRanking.save}
            </Button>
          </div>
        )}
      </section>
    </ProfileAccordionSection>
  );
}
